import { z } from "zod";

// Verify User Schema (for step one of reset password)
export const verifyUserSchema = z.object({
  phoneNumber: z
    .string()
    .min(10, "Phone number must be 10 digits")
    .max(10, "Phone number must be 10 digits")
    .regex(/^\d+$/, "Phone number must contain only numbers"),
  otp: z
    .string()
    .min(4, "OTP must be 4 digits")
    .max(4, "OTP must be 4 digits")
    .regex(/^\d+$/, "OTP must contain only numbers"),
});

// Conditional Verify User Schema Factory
export const createVerifyUserSchema = (hasSelectedSchool: boolean) => {
  return z.object({
    phoneNumber: hasSelectedSchool
      ? z.string().min(1, "Phone number is required") // Minimal validation when school is selected
      : z
          .string()
          .min(10, "Phone number must be 10 digits")
          .max(10, "Phone number must be 10 digits")
          .regex(/^\d+$/, "Phone number must contain only numbers"),
    otp: z
      .string()
      .min(4, "OTP must be 4 digits")
      .max(4, "OTP must be 4 digits")
      .regex(/^\d+$/, "OTP must contain only numbers"),
  });
};

// Reset Password Schema (for step two - setting new password)
export const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(50, "Password is too long")
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

// Export types
export type VerifyUserData = z.infer<typeof verifyUserSchema>;
export type ResetPasswordData = z.infer<typeof resetPasswordSchema>;

// Validation helper functions
export const validateVerifyUser = (
  data: unknown,
  hasSelectedSchool?: boolean
) => {
  const schema =
    hasSelectedSchool !== undefined
      ? createVerifyUserSchema(hasSelectedSchool)
      : verifyUserSchema;
  return schema.safeParse(data);
};

export const validateResetPassword = (data: unknown) => {
  return resetPasswordSchema.safeParse(data);
};
